/**
 * Context Collector – gathers contextual data for feedback entries.
 *
 * Pure utility functions that collect page URL, page name, user ID,
 * app version, device type, browser, status, and a unique ID.
 *
 * Validates: Requirements 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7, 4.8, 4.9
 */

import { getTitle } from '../services/page-title.service';
import { decodeTokenPayload } from '../../features/auth/auth.service';
import { storageAdapter } from '../../infrastructure/storage-adapter';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CollectedContext {
  page_url: string;
  page_name: string;
  user_id: string;
  app_version: string;
  device_type: 'mobile' | 'desktop';
  browser: string;
  status: 'new';
  id: string;
}

// ---------------------------------------------------------------------------
// UUID Generation
// ---------------------------------------------------------------------------

/**
 * Generate a UUID v4 string.
 *
 * Uses `crypto.randomUUID()` when available, otherwise falls back to a
 * `Math.random()`-based implementation.
 */
export function generateUUID(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to manual generation
  }

  // Fallback: Math.random()-based UUID v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ---------------------------------------------------------------------------
// Device Type Detection
// ---------------------------------------------------------------------------

/**
 * Detect whether the current device is mobile or desktop.
 *
 * Uses `navigator.userAgent` pattern matching and `navigator.maxTouchPoints`
 * as a secondary signal. Returns `'desktop'` when navigator is unavailable.
 */
export function detectDeviceType(): 'mobile' | 'desktop' {
  try {
    if (typeof navigator === 'undefined') {
      return 'desktop';
    }

    const ua = navigator.userAgent || '';
    const mobilePattern = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

    if (mobilePattern.test(ua)) {
      return 'mobile';
    }

    // iPad with desktop-class browsing reports as Mac but has touch points
    if (navigator.maxTouchPoints > 0 && /Macintosh/i.test(ua)) {
      return 'mobile';
    }

    return 'desktop';
  } catch {
    return 'desktop';
  }
}

// ---------------------------------------------------------------------------
// Browser Detection
// ---------------------------------------------------------------------------

/**
 * Detect the browser name from `navigator.userAgent`.
 *
 * Returns `'Unknown'` when the user agent is unavailable or unrecognised.
 */
export function detectBrowser(): string {
  try {
    if (typeof navigator === 'undefined' || !navigator.userAgent) {
      return 'Unknown';
    }

    const ua = navigator.userAgent;

    // Order matters – check more specific strings first.
    if (ua.includes('Edg/')) return 'Edge';
    if (ua.includes('OPR/') || ua.includes('Opera')) return 'Opera';
    if (ua.includes('Chrome') && !ua.includes('Edg/') && !ua.includes('OPR/')) return 'Chrome';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('MSIE') || ua.includes('Trident/')) return 'Internet Explorer';

    return 'Unknown';
  } catch {
    return 'Unknown';
  }
}

// ---------------------------------------------------------------------------
// Collect All Context
// ---------------------------------------------------------------------------

/**
 * Collect all contextual data for a feedback entry.
 *
 * Gathers page URL, page name, username (from JWT), app version,
 * device type, browser, status, and a unique ID.
 */
export function collectContext(): CollectedContext {
  // user_id from JWT token (use readable username instead of technical ID)
  let userId = '';
  try {
    const token = storageAdapter.getAccessToken();
    if (token) {
      const payload = decodeTokenPayload(token);
      if (payload?.name) {
        userId = payload.name;
      } else if (payload?.sub) {
        userId = payload.sub;
      }
    }
  } catch {
    // no token available – userId stays empty
  }

  return {
    page_url: typeof window !== 'undefined' ? window.location.hash : '',
    page_name: getTitle(),
    user_id: userId,
    app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '',
    device_type: detectDeviceType(),
    browser: detectBrowser(),
    status: 'new',
    id: generateUUID(),
  };
}
